import React from 'react';
import { CircleMarker, Marker } from 'react-leaflet';
import L from 'leaflet';

interface LocationIndicatorProps {
  latitude: number;
  longitude: number;
  accuracy: number;
  heading?: number;
  timestamp: number;
}

// Custom icon for current location
const createLocationIcon = (heading?: number) => {
  const size = 20;
  const color = '#007bff'; // Blue color for current location
  
  // Create arrow shape pointing in the heading direction
  const arrowSvg = heading !== undefined && heading !== null ? `
    <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg">
      <g transform="translate(${size/2}, ${size/2}) rotate(${heading})">
        <path d="M 0,-8 L 4,6 L 0,2 L -4,6 Z" fill="${color}" stroke="white" stroke-width="1"/>
      </g>
      <circle cx="${size/2}" cy="${size/2}" r="2" fill="white" stroke="${color}" stroke-width="1"/>
    </svg>
  ` : `
    <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg">
      <circle cx="${size/2}" cy="${size/2}" r="8" fill="${color}" stroke="white" stroke-width="2" opacity="0.9"/>
      <circle cx="${size/2}" cy="${size/2}" r="3" fill="white"/>
    </svg>
  `;
  
  return L.divIcon({
    html: arrowSvg,
    className: 'current-location-marker',
    iconSize: [size, size],
    iconAnchor: [size/2, size/2],
  });
};

// Accuracy circle component
const AccuracyCircle: React.FC<LocationIndicatorProps> = ({ latitude, longitude, accuracy }) => {
  // Don't show accuracy circle if accuracy is very poor or unknown
  if (!accuracy || accuracy > 200) {
    return null;
  }
  
  // Color based on accuracy quality
  const getAccuracyColor = (acc: number): string => {
    if (acc <= 5) return '#00ff00';      // Green - excellent
    if (acc <= 10) return '#7fff00';     // Light green - very good  
    if (acc <= 20) return '#ffff00';     // Yellow - good
    if (acc <= 50) return '#ff7f00';     // Orange - fair
    return '#ff0000';                    // Red - poor
  };
  
  return (
    <CircleMarker
      center={[latitude, longitude]}
      radius={Math.min(Math.max(accuracy / 2, 5), 50)} // Scale radius appropriately
      pathOptions={{
        color: getAccuracyColor(accuracy),
        fillColor: getAccuracyColor(accuracy),
        fillOpacity: 0.15,
        weight: 2,
        opacity: 0.7
      }}
    />
  );
};

export const LocationIndicator: React.FC<LocationIndicatorProps> = (props) => {
  const { latitude, longitude, heading } = props;
  
  return (
    <>
      {/* Accuracy circle */}
      <AccuracyCircle {...props} />
      
      {/* Current location marker with heading */}
      <Marker
        position={[latitude, longitude]}
        icon={createLocationIcon(heading)}
        zIndexOffset={1000} // Ensure it appears on top
      />
    </>
  );
};

export default LocationIndicator;